import { api, ApiError, getPage, uploadChatAttachment, ALLOWED_IMAGE_TYPES, CHAT_FILE_EXTENSIONS } from "./api.js";
import { esc, linkify, displayName, avatarHtml, formatDateTime, toast, cohortIcons } from "./utils.js";
import { getCurrentUser } from "./auth.js";
import { confirmAction } from "./confirmDialog.js";
import { reportDialog } from "./reportDialog.js";
import { openPhotoViewer } from "./photoViewer.js";

/**
 * One cohort's chat, as the right-hand frame of the Cohorts page
 * (views/communities.js), which owns the live connection and passes this
 * chat's events in.
 *
 * Messages are sent, edited and deleted through the API. History pages in
 * upwards ("Load earlier messages"); after a dropped connection, what was
 * missed is fetched with ?after=.
 *
 * The + beside the box attaches up to MAX_ATTACHMENTS photos and files to a
 * message (uploaded when it's sent). In the chat, photos are a grid of tiles,
 * three across, each enlarging over the page; files are a tile with their
 * type's icon and name, which downloads them. Their links stop working at
 * expires_at, so the chat fetches a message again for fresh ones shortly before
 * (checking every minute, when the page is seen again, and on a click).
 */

const PAGE = 30;
/** How long "is typing" stays up without another sign of it. */
const TYPING_SHOWN_MS = 5000;
/** Caught up from a few messages back: two sent at once can be saved in either
 *  order, so the newest we have isn't a safe starting point on its own. */
const CATCH_UP_OVERLAP = 5;
/** Close enough to the bottom to count as reading the newest messages. */
const NEAR_BOTTOM_PX = 80;

/** "Seen by Ana", "Ana and Ben", "Ana, Ben and Cara", "Ana, Ben and 2 others". */
export function seenByText(names) {
  if (!names.length) return "";
  if (names.length === 1) return `Seen by ${names[0]}`;
  if (names.length === 2) return `Seen by ${names[0]} and ${names[1]}`;
  if (names.length === 3) return `Seen by ${names[0]}, ${names[1]} and ${names[2]}`;
  return `Seen by ${names[0]}, ${names[1]} and ${names.length - 2} others`;
}

/** "Ana is typing…", "Ana and Ben are typing…", "Several people are typing…". */
export function typingText(names) {
  if (!names.length) return "";
  if (names.length === 1) return `${names[0]} is typing…`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are typing…`;
  return "Several people are typing…";
}

export function deletedLabel(message) {
  return message.deleted_by_admin ? "Deleted by an admin" : "Message deleted";
}

/** How long before its link expires an attachment counts as needing a fresh
 *  one: time to be clicked and loaded. */
const LINK_MARGIN_MS = 5 * 60 * 1000;
/** How often the open chat looks for links about to expire. */
export const LINK_CHECK_MS = 60 * 1000;

/** Whether any of the message's photos and files has a link that has expired,
 *  or soon will. */
export function linksExpiring(message, now = Date.now()) {
  return (message.attachments || []).some((a) =>
    a.url && a.expires_at && Date.parse(a.expires_at) - now < LINK_MARGIN_MS);
}

/** Mirrors MAX_ATTACHMENTS in backend/app/services/chats.py. */
export const MAX_ATTACHMENTS = 10;
const ACCEPT = [...ALLOWED_IMAGE_TYPES, ...CHAT_FILE_EXTENSIONS.map((e) => `.${e}`)].join(",");

const FILE_ICONS = {
  pdf: "bi-file-earmark-pdf",
  doc: "bi-file-earmark-word", docx: "bi-file-earmark-word",
  xls: "bi-file-earmark-excel", xlsx: "bi-file-earmark-excel", csv: "bi-file-earmark-spreadsheet",
  ppt: "bi-file-earmark-ppt", pptx: "bi-file-earmark-ppt",
  txt: "bi-file-earmark-text",
  gpx: "bi-geo-alt",
  uddf: "bi-file-earmark-bar-graph", ssrf: "bi-file-earmark-bar-graph", fit: "bi-file-earmark-bar-graph",
};

/** The icon for a file of this type (its extension). */
export function fileIcon(extension) {
  return FILE_ICONS[extension] || "bi-file-earmark";
}

/** What a message's photos and files come to, in a line: "Photo", "3 photos",
 *  "plan.pdf", "2 files", "4 attachments". */
export function attachmentSummary(attachments = []) {
  const photos = attachments.filter((a) => a.kind === "photo").length;
  const files = attachments.length - photos;
  if (!attachments.length) return "";
  if (!files) return photos === 1 ? "Photo" : `${photos} photos`;
  if (!photos) return files === 1 ? attachments[0].name || "File" : `${files} files`;
  return `${attachments.length} attachments`;
}

/** A message's photos (a grid, three across) and files (a tile each). */
export function attachmentsHtml(attachments = []) {
  const photos = attachments.filter((a) => a.kind === "photo");
  const files = attachments.filter((a) => a.kind !== "photo");
  const tiles = photos.map((a) => a.url
    ? `<button type="button" class="dc-chat-photo" data-photo-url="${esc(a.url)}" data-attachment-id="${a.id}"
         aria-label="Enlarge this photo">
         <img src="${esc(a.url)}" alt="" loading="lazy" /></button>`
    : `<div class="dc-chat-photo dc-chat-photo-missing" title="This photo can't be shown right now">
         <i class="bi bi-image"></i></div>`).join("");
  const fileTiles = files.map((a) => {
    const type = (a.extension || "").toUpperCase();
    const inner = `<i class="bi ${fileIcon(a.extension)}" aria-hidden="true"></i>
      <span class="dc-min-w-0"><span class="d-block text-truncate dc-chat-file-name">${esc(a.name || "File")}</span>
      <span class="d-block dc-chat-file-type">${esc(type)}</span></span>`;
    return a.url
      ? `<a class="dc-chat-file" href="${esc(a.url)}" target="_blank" rel="noopener" download data-attachment-id="${a.id}"
            title="Download ${esc(a.name || "this file")}">${inner}</a>`
      : `<div class="dc-chat-file" title="This file can't be downloaded right now">${inner}</div>`;
  }).join("");
  return `${tiles ? `<div class="dc-chat-photos">${tiles}</div>` : ""}${
    fileTiles ? `<div class="dc-chat-files">${fileTiles}</div>` : ""}`;
}

/**
 * Shows `cohort`'s chat in `host`. `socket` is the page's (chatSocket.js);
 * `onRead(chatId)` is told when this diver has read the chat to the end.
 * `onBack`, when given, adds a back arrow to the header for narrow screens,
 * where the chat fills the page instead of sitting beside the list.
 * Resolves to a controller: handleEvent, subscribed, closed, connection,
 * destroy.
 */
export async function mountChatPanel(host, { cohort, socket, onRead = () => {}, onBack = null }) {
  const me = getCurrentUser();
  host.innerHTML = `<div class="text-center py-5"><div class="spinner-border text-primary"></div></div>`;
  const inert = { handleEvent() {}, subscribed() {}, closed() {}, connection() {}, destroy() {} };

  if (!cohort.chat) {
    host.innerHTML = `
      <div class="dc-empty" id="chat-members-only"><i class="bi bi-lock"></i>
        <div class="fw-semibold">This chat is for the cohort's members</div>
        <div>Join <a href="#/cohort/${cohort.id}">${esc(cohort.name)}</a> to take part.</div>
      </div>`;
    return inert;
  }

  let chat, firstPage;
  try {
    [chat, firstPage] = await Promise.all([
      api.get(`/chats/${cohort.chat.id}`),
      getPage(`/chats/${cohort.chat.id}/messages`, { limit: PAGE }),
    ]);
  } catch (err) {
    host.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>${
      esc(err.message || "Could not load the chat.")}</div></div>`;
    return inert;
  }

  const isAdmin = cohort.my_role === "admin";
  const messages = new Map(firstPage.items.map((m) => [m.id, m]));
  let nextCursor = firstPage.nextCursor;
  /** user id -> { user, last_read_message_id }, for the others who share it. */
  const readers = new Map(chat.readers.map((r) => [r.user.id, r]));
  let lastReadSent = chat.last_read_message_id ?? 0;
  /** user id -> { name, timer } for whoever is typing now. */
  const typists = new Map();
  let closed = false;
  let destroyed = false;

  host.innerHTML = `
    <div class="dc-chat" data-chat-id="${chat.id}">
      <div class="d-flex justify-content-between align-items-center gap-2 px-3 py-2 border-bottom">
        <div class="d-flex align-items-center gap-2 dc-min-w-0">
          ${onBack ? `<button type="button" class="btn btn-link p-0 d-lg-none flex-shrink-0" id="chat-back"
                              aria-label="Back to your cohorts"><i class="bi bi-arrow-left fs-5"></i></button>` : ""}
          <a href="#/cohort/${cohort.id}" class="fw-semibold text-decoration-none text-truncate" id="chat-cohort-link"
             title="Open ${esc(cohort.name)}'s page">${cohortIcons(cohort)}${esc(cohort.name)}</a>
        </div>
        <div class="form-check form-switch mb-0 flex-shrink-0" title="Let the cohort's members see how far you've read">
          <input class="form-check-input" type="checkbox" role="switch" id="read-receipts-switch"
            ${chat.read_receipts ? "checked" : ""} />
          <label class="form-check-label small" for="read-receipts-switch">Read receipts</label>
        </div>
      </div>
      <div class="small text-slate dc-chat-status px-3" id="chat-status"></div>
      <div class="dc-chat-messages" id="chat-scroller">
        <div class="text-center my-2" id="chat-older"></div>
        <div id="chat-list"></div>
        <div class="small text-slate text-end px-3 pb-1" id="chat-seen"></div>
      </div>
      <div class="border-top p-2">
        <div class="small text-slate dc-chat-typing px-1" id="chat-typing"></div>
        <div class="dc-chat-pending d-none" id="chat-pending"></div>
        <form class="d-flex gap-2 align-items-end" id="chat-form">
          <button class="btn btn-outline-secondary" type="button" id="chat-attach"
            aria-label="Attach photos or files" title="Attach photos or files"><i class="bi bi-plus-lg"></i></button>
          <input type="file" class="d-none" id="chat-file" multiple accept="${ACCEPT}" />
          <textarea class="form-control" id="chat-input" rows="1" maxlength="4000"
            placeholder="Message ${esc(cohort.name)}" aria-label="Message"></textarea>
          <button class="btn btn-primary" type="submit" id="chat-send" aria-label="Send">
            <i class="bi bi-send"></i></button>
        </form>
        <div class="text-danger small mt-1 d-none" id="chat-error"></div>
      </div>
    </div>`;

  const scroller = host.querySelector("#chat-scroller");
  const list = host.querySelector("#chat-list");
  const olderHost = host.querySelector("#chat-older");
  const seenHost = host.querySelector("#chat-seen");
  const typingHost = host.querySelector("#chat-typing");
  const statusHost = host.querySelector("#chat-status");
  const form = host.querySelector("#chat-form");
  const input = host.querySelector("#chat-input");
  const sendBtn = host.querySelector("#chat-send");
  const errorHost = host.querySelector("#chat-error");
  const attachBtn = host.querySelector("#chat-attach");
  const fileInput = host.querySelector("#chat-file");
  const pendingHost = host.querySelector("#chat-pending");
  /** What's attached to the message being written: { id, file, photo, preview }. */
  let pending = [];
  let pendingId = 0;
  const receiptsSwitch = host.querySelector("#read-receipts-switch");
  host.querySelector("#chat-back")?.addEventListener("click", () => onBack());

  const ordered = () => [...messages.values()].sort((a, b) => a.id - b.id);
  const nearBottom = () => scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight < NEAR_BOTTOM_PX;
  const toBottom = () => {
    scroller.scrollTop = scroller.scrollHeight;
  };

  function messageHtml(m) {
    const mine = m.author?.id === me?.id;
    const author = displayName(m.author);
    const attachments = m.is_deleted ? [] : m.attachments || [];
    // Photos or files alone have no text to edit.
    const canEdit = mine && !m.is_deleted && !!m.content;
    const canDelete = !m.is_deleted && (mine || isAdmin);
    // Someone else's, to the site's moderators: an admin may report too.
    const canReport = !mine && !m.is_deleted;
    const menu = canEdit || canDelete || canReport
      ? `<div class="dropdown dc-msg-menu">
          <button class="btn btn-link btn-sm p-0 text-slate" type="button" data-bs-toggle="dropdown"
                  aria-expanded="false" aria-label="Manage this message"><i class="bi bi-three-dots"></i></button>
          <div class="dropdown-menu dropdown-menu-end">
            ${canEdit ? `<button class="dropdown-item" type="button" data-action="edit" data-id="${m.id}">Edit</button>` : ""}
            ${canDelete ? `<button class="dropdown-item text-danger" type="button" data-action="delete" data-id="${m.id}">${
              mine ? "Delete" : "Delete as admin"}</button>` : ""}
            ${canReport ? `<button class="dropdown-item" type="button" data-action="report" data-id="${m.id}">Report</button>` : ""}
          </div>
        </div>`
      : "";
    const body = m.is_deleted
      ? `<div class="fst-italic text-slate dc-msg-deleted">${deletedLabel(m)}</div>`
      : m.content ? `<div class="dc-user-text dc-msg-text">${linkify(m.content)}</div>` : "";
    return `
      <div class="dc-msg ${mine ? "dc-msg-mine" : ""}" data-message-id="${m.id}">
        ${mine ? "" : avatarHtml(author, { sizePx: 32, fontSize: "0.85rem", className: "me-2" })}
        <div class="dc-msg-body">
          <div class="small text-slate dc-msg-meta">
            ${mine ? "" : `<span class="fw-semibold">${esc(author)}</span> · `}${formatDateTime(m.created_at)}${
              m.is_edited && !m.is_deleted ? ` · <span class="dc-msg-edited">edited</span>` : ""}
          </div>
          <div class="d-flex align-items-start gap-1">
            <div class="dc-min-w-0">
              ${body ? `<div class="dc-msg-bubble">${body}</div>` : ""}
              ${attachments.length ? attachmentsHtml(attachments) : ""}
            </div>
            ${menu}
          </div>
        </div>
      </div>`;
  }

  function renderSeen() {
    const all = ordered();
    const last = all[all.length - 1];
    if (!last) {
      seenHost.textContent = "";
      return;
    }
    const names = [...readers.values()]
      .filter((r) => r.last_read_message_id != null && r.last_read_message_id >= last.id && r.user.id !== last.author?.id)
      .map((r) => displayName(r.user));
    seenHost.textContent = seenByText(names);
  }

  function renderList() {
    const all = ordered();
    list.innerHTML = all.length
      ? all.map(messageHtml).join("")
      : `<p class="text-slate text-center my-4" id="chat-empty">No messages yet. Say hello to ${esc(cohort.name)}.</p>`;
    olderHost.innerHTML = nextCursor
      ? `<button class="btn btn-sm btn-outline-secondary" type="button" id="chat-load-older">Load earlier messages</button>`
      : "";
    renderSeen();
  }

  function renderTyping() {
    typingHost.textContent = typingText([...typists.values()].map((t) => t.name));
  }

  function setStatus(text) {
    statusHost.textContent = text;
  }

  /** Adds or replaces messages (from a page, an event, or our own send). */
  function upsert(items, { stickToBottom = nearBottom() } = {}) {
    for (const m of items) messages.set(m.id, m);
    renderList();
    if (stickToBottom) toBottom();
  }

  /** Marks the newest message read, when the reader can see it. */
  function markRead() {
    if (closed || destroyed || document.visibilityState === "hidden" || !nearBottom()) return;
    const all = ordered();
    const newest = all[all.length - 1];
    if (!newest || newest.id <= lastReadSent) return;
    lastReadSent = newest.id;
    onRead(chat.id);
    api.post(`/chats/${chat.id}/read`, { message_id: newest.id }).catch(() => {
      // Not worth interrupting the chat for: the next message read tries again.
      lastReadSent = 0;
    });
  }

  async function loadOlder() {
    const button = olderHost.querySelector("#chat-load-older");
    if (button) button.disabled = true;
    try {
      const page = await getPage(`/chats/${chat.id}/messages`, { limit: PAGE, cursor: nextCursor });
      if (destroyed) return;
      nextCursor = page.nextCursor;
      // Keep what the reader was looking at in place as earlier messages go in above it.
      const fromBottom = scroller.scrollHeight - scroller.scrollTop;
      upsert(page.items, { stickToBottom: false });
      scroller.scrollTop = scroller.scrollHeight - fromBottom;
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not load earlier messages.", "danger");
      if (button) button.disabled = false;
    }
  }

  async function catchUp() {
    const all = ordered();
    const from = all.length > CATCH_UP_OVERLAP ? all[all.length - 1 - CATCH_UP_OVERLAP].id : 0;
    try {
      const [page, state] = await Promise.all([
        getPage(`/chats/${chat.id}/messages`, { query: { after: from }, limit: 100 }),
        api.get(`/chats/${chat.id}`),
      ]);
      if (destroyed) return;
      readers.clear();
      for (const r of state.readers) readers.set(r.user.id, r);
      upsert(page.items);
      markRead();
    } catch {
      setStatus("Some messages may be missing. Reload the page to see them all.");
    }
  }

  function showTyping(user) {
    if (!user || user.id === me?.id) return;
    clearTimeout(typists.get(user.id)?.timer);
    typists.set(user.id, {
      name: displayName(user),
      timer: setTimeout(() => {
        typists.delete(user.id);
        renderTyping();
      }, TYPING_SHOWN_MS),
    });
    renderTyping();
  }

  function stopTyping(userId) {
    const t = typists.get(userId);
    if (!t) return;
    clearTimeout(t.timer);
    typists.delete(userId);
    renderTyping();
  }

  function shutChat(message) {
    closed = true;
    input.disabled = true;
    sendBtn.disabled = true;
    attachBtn.disabled = true;
    setStatus(message);
  }

  function showError(text) {
    errorHost.textContent = text;
    errorHost.classList.toggle("d-none", !text);
  }

  function renderPending() {
    pendingHost.classList.toggle("d-none", !pending.length);
    pendingHost.innerHTML = pending.map((p) => `
      <div class="dc-pending-item ${p.photo ? "dc-pending-photo" : "dc-pending-file"}" data-pending-id="${p.id}">
        ${p.photo
          ? `<img src="${esc(p.preview)}" alt="${esc(p.file.name)}" />`
          : `<i class="bi ${fileIcon(p.extension)}" aria-hidden="true"></i>
             <span class="text-truncate small">${esc(p.file.name)}</span>`}
        <button type="button" class="btn btn-sm btn-danger p-0 lh-1 rounded-circle dc-pending-remove"
                data-remove-id="${p.id}" aria-label="Remove ${esc(p.file.name)}">&times;</button>
      </div>`).join("");
  }

  function clearPending() {
    for (const p of pending) if (p.preview) URL.revokeObjectURL(p.preview);
    pending = [];
    renderPending();
  }

  attachBtn.addEventListener("click", () => fileInput.click());

  fileInput.addEventListener("change", () => {
    const chosen = [...fileInput.files];
    fileInput.value = ""; // so choosing the same file again still counts
    const room = MAX_ATTACHMENTS - pending.length;
    showError(chosen.length > room
      ? `You can attach at most ${MAX_ATTACHMENTS} photos or files to a message.` : "");
    for (const file of chosen.slice(0, Math.max(0, room))) {
      const photo = ALLOWED_IMAGE_TYPES.includes(file.type);
      const dot = file.name.lastIndexOf(".");
      pending.push({
        id: ++pendingId, file, photo,
        extension: dot > 0 ? file.name.slice(dot + 1).toLowerCase() : "",
        preview: photo ? URL.createObjectURL(file) : null,
      });
    }
    renderPending();
    input.focus();
  });

  pendingHost.addEventListener("click", (e) => {
    const button = e.target.closest("[data-remove-id]");
    if (!button) return;
    const gone = pending.find((p) => p.id === Number(button.dataset.removeId));
    if (gone?.preview) URL.revokeObjectURL(gone.preview);
    pending = pending.filter((p) => p !== gone);
    renderPending();
  });

  // ---- what the reader does ----

  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const content = input.value.trim();
    if ((!content && !pending.length) || closed) return;
    showError("");
    sendBtn.disabled = true;
    attachBtn.disabled = true;
    try {
      // Uploaded as it's sent, straight to storage; then the message names them.
      const attachments = await Promise.all(pending.map((p) => uploadChatAttachment(p.file)));
      const message = await api.post(`/chats/${chat.id}/messages`, {
        content, ...(attachments.length ? { attachments } : {}),
      });
      if (destroyed) return;
      input.value = "";
      clearPending();
      upsert([message], { stickToBottom: true });
      // Sending reads up to it, on the server too.
      lastReadSent = Math.max(lastReadSent, message.id);
    } catch (err) {
      // An unconfirmed email opens its own dialog (api.js); say nothing twice.
      if (!(err instanceof ApiError && err.code === "email_not_verified")) {
        showError(err instanceof ApiError ? err.message : "Your message wasn't sent. Try again.");
      }
    } finally {
      sendBtn.disabled = closed;
      attachBtn.disabled = closed;
      if (!destroyed) input.focus();
    }
  });

  input.addEventListener("keydown", (e) => {
    // Enter sends; Shift+Enter starts a new line, as in most chats.
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      form.requestSubmit ? form.requestSubmit() : form.dispatchEvent(new Event("submit", { cancelable: true }));
    }
  });

  input.addEventListener("input", () => socket?.typing(chat.id));

  olderHost.addEventListener("click", (e) => {
    if (e.target.closest("#chat-load-older")) loadOlder();
  });

  scroller.addEventListener("scroll", () => markRead());

  /** The message again, with fresh links to its photos and files. */
  async function freshMessage(id) {
    const message = await api.get(`/chats/${chat.id}/messages/${id}`);
    if (!destroyed) upsert([message], { stickToBottom: false });
    return message;
  }

  /** Fetches fresh links for the messages whose links are about to expire. Not
   *  while a message is being edited: redrawing the list would lose the edit. */
  async function refreshLinks() {
    if (destroyed || document.visibilityState === "hidden" || list.querySelector(".dc-msg-edit")) return;
    for (const message of ordered().filter((m) => linksExpiring(m))) {
      try {
        await freshMessage(message.id);
      } catch {
        // Tried again at the next check, or when it's clicked.
      }
      if (destroyed) return;
    }
  }
  const linkTimer = setInterval(refreshLinks, LINK_CHECK_MS);

  list.addEventListener("click", async (e) => {
    const photo = e.target.closest("[data-photo-url]");
    const file = e.target.closest("a.dc-chat-file");
    if (photo || file) {
      const element = photo || file;
      const message = messages.get(Number(element.closest("[data-message-id]")?.dataset.messageId));
      if (!message || !linksExpiring(message)) {
        // Fresh: a photo opens at once, and a file's link downloads it as it is.
        if (photo) openPhotoViewer(photo.dataset.photoUrl);
        return;
      }
      // Stale: a file's old link would open storage's "access denied" page, so
      // both get a fresh link first.
      e.preventDefault();
      try {
        const fresh = await freshMessage(message.id);
        const id = Number(element.dataset.attachmentId);
        const url = (fresh.attachments || []).find((a) => a.id === id)?.url;
        if (!url) throw new Error("no link");
        if (photo) openPhotoViewer(url);
        else location.assign(url); // a download: the page stays
      } catch (err) {
        toast(err instanceof ApiError ? err.message : `Could not open that ${photo ? "photo" : "file"}. Try again.`,
          "danger");
      }
      return;
    }
    const target = e.target.closest("[data-action]");
    if (!target) return;
    const message = messages.get(Number(target.dataset.id));
    if (!message) return;
    if (target.dataset.action === "edit") startEditing(message);
    if (target.dataset.action === "delete") deleteMessage(message);
    if (target.dataset.action === "report") reportMessage(message);
  });

  /** To the site's moderators, who see this message (and only this one). */
  async function reportMessage(message) {
    const answer = await reportDialog({
      what: "message", snippet: message.content || attachmentSummary(message.attachments),
    });
    if (!answer || destroyed) return;
    try {
      await api.post("/reports", { chat_message_id: message.id, ...answer });
      // Same message whether this was new or a duplicate, as for posts.
      toast("Thanks — that's been sent to the moderators.", "success");
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not send that report.", "danger");
    }
  }

  function startEditing(message) {
    const row = list.querySelector(`[data-message-id="${message.id}"] .dc-msg-bubble`);
    if (!row) return;
    row.innerHTML = `
      <form class="dc-msg-edit" data-edit-id="${message.id}">
        <textarea class="form-control form-control-sm mb-1" rows="2" maxlength="4000"
          aria-label="Edit your message">${esc(message.content)}</textarea>
        <div class="d-flex gap-1 justify-content-end">
          <button class="btn btn-sm btn-outline-secondary" type="button" data-edit-cancel>Cancel</button>
          <button class="btn btn-sm btn-primary" type="submit">Save</button>
        </div>
        <div class="text-danger small d-none" data-edit-error></div>
      </form>`;
    const editForm = row.querySelector("form");
    const field = editForm.querySelector("textarea");
    field.focus();
    editForm.querySelector("[data-edit-cancel]").addEventListener("click", () => renderList());
    editForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      const save = editForm.querySelector("[type=submit]");
      save.disabled = true;
      try {
        const updated = await api.patch(`/chats/${chat.id}/messages/${message.id}`, { content: field.value });
        if (!destroyed) upsert([updated], { stickToBottom: false });
      } catch (err) {
        const error = editForm.querySelector("[data-edit-error]");
        error.textContent = err instanceof ApiError ? err.message : "Your change wasn't saved. Try again.";
        error.classList.remove("d-none");
        save.disabled = false;
      }
    });
  }

  async function deleteMessage(message) {
    const mine = message.author?.id === me?.id;
    const ok = await confirmAction({
      title: mine ? "Delete this message?" : "Delete this message as an admin?",
      body: mine
        ? "Everyone will see “Message deleted” in its place."
        : "Everyone will see “Deleted by an admin” in its place.",
      confirmLabel: "Delete",
    });
    if (!ok || destroyed) return;
    try {
      await api.delete(`/chats/${chat.id}/messages/${message.id}`);
      if (!destroyed) {
        upsert([{ ...message, content: null, attachments: [], is_deleted: true, deleted_by_admin: !mine }],
          { stickToBottom: false });
      }
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not delete that message.", "danger");
    }
  }

  receiptsSwitch.addEventListener("change", async () => {
    const on = receiptsSwitch.checked;
    receiptsSwitch.disabled = true;
    try {
      const updated = await api.patch(`/chats/${chat.id}/settings`, { read_receipts: on });
      receiptsSwitch.checked = updated.read_receipts === true;
    } catch (err) {
      receiptsSwitch.checked = !on;
      toast(err instanceof ApiError ? err.message : "Could not change your read receipts.", "danger");
    } finally {
      receiptsSwitch.disabled = false;
    }
  });

  const onVisible = () => {
    if (document.visibilityState !== "visible") return;
    markRead();
    // Back after a while (a laptop asleep): links may have expired meanwhile.
    refreshLinks();
  };
  document.addEventListener("visibilitychange", onVisible);

  renderList();
  toBottom();
  markRead();

  return {
    chatId: chat.id,
    /** An event for this chat, from the page's connection. */
    handleEvent(event) {
      if (destroyed || event.chat_id !== chat.id) return;
      if (event.type === "message.created") {
        stopTyping(event.message.author?.id);
        upsert([event.message]);
        markRead();
      } else if (event.type === "message.edited" || event.type === "message.deleted") {
        if (messages.has(event.message.id)) upsert([event.message], { stickToBottom: false });
      } else if (event.type === "read") {
        if (event.user.id === me?.id) return; // our own, from another tab or device
        if (event.last_read_message_id == null) readers.delete(event.user.id);
        else readers.set(event.user.id, { user: event.user, last_read_message_id: event.last_read_message_id });
        renderSeen();
      } else if (event.type === "typing") {
        showTyping(event.user);
      }
    },
    /** The connection is watching this chat; `again` after a gap. */
    subscribed({ again } = {}) {
      if (!destroyed && again) catchUp();
    },
    /** The server shut this chat to us. */
    closed(detail) {
      if (!destroyed) shutChat(detail?.error || "This chat is no longer available to you.");
    },
    /** How the page's connection is doing. */
    connection(status) {
      if (destroyed || closed) return;
      if (status === "live") setStatus("");
      else if (status === "reconnecting") setStatus("Reconnecting…");
      else if (status === "refused") setStatus("Sign in again to see new messages as they arrive.");
      else if (status === "unavailable") {
        setStatus("New messages won't appear by themselves in this browser: reload to see them.");
      }
    },
    destroy() {
      destroyed = true;
      clearInterval(linkTimer);
      clearPending();
      for (const t of typists.values()) clearTimeout(t.timer);
      typists.clear();
      document.removeEventListener("visibilitychange", onVisible);
    },
  };
}
